import { useState, useRef } from 'react'
import { useMutation, useQuery } from 'react-query'
import { gql } from 'graphql-request'

import graphQLRequest from 'CanExConfig/graphQLRequest'

export const resumeParsingStatuses = {
  QUEUED: 'QUEUED',
  COMPLETED: 'COMPLETED',
  ERRORED: 'ERRORED',
  FAILED: 'FAILED',
}

const CREATE_RESUME_PARSING_EXECUTION = gql`
  mutation createResumeParsingExecution($candidateProfileId: ID!, $file: Upload!, $source: String!) {
    createResumeParsingExecution(input: { candidateProfileId: $candidateProfileId, file: $file, source: $source }) {
      resumeParsingExecution {
        id
        parsedEducation
        parsedOnlineProfile
        parsedPhone
        parsedSkills
        parsedWorkHistory
        status
      }
    }
  }
`

const FETCH_RESUME_PARSING_EXECUTION = gql`
  query fetchResumeParsingExecution($candidateProfileId: ID!, $resumeParsingExecutionId: ID!) {
    candidateProfile(id: $candidateProfileId) {
      resumeParsingExecution(resumeParsingExecutionId: $resumeParsingExecutionId) {
        id
        parsedEducation
        parsedOnlineProfile
        parsedPhone
        parsedSkills
        parsedWorkHistory
        status
      }
    }
  }
`

const POLL_LIMIT = 60

export const useParseResume = (candidateProfileId, source) => {
  const [resumeParsingExecutionId, setResumeParsingExecutionId] = useState()
  const [resumeParsingExecutionStatus, setResumeParsingExecutionStatus] = useState()
  const fetchPollCount = useRef(0)

  const { mutateAsync: importResume, isLoading: isImportingResume } = useMutation(
    'importResume',
    variables =>
      graphQLRequest(CREATE_RESUME_PARSING_EXECUTION, {
        candidateProfileId,
        source,
        ...variables,
      }),
    {
      onMutate: () => {
        fetchPollCount.current = 0
      },
      onSuccess: ({ createResumeParsingExecution: { resumeParsingExecution } }) => {
        setResumeParsingExecutionId(resumeParsingExecution.id)
        setResumeParsingExecutionStatus(resumeParsingExecution.status)
      },
    },
  )

  const isQueued = resumeParsingExecutionStatus === resumeParsingStatuses.QUEUED
  const isUnderPollLimit = fetchPollCount.current < POLL_LIMIT

  const pollingEnabled = !!resumeParsingExecutionId && isQueued && isUnderPollLimit

  const { data: resumeParsingExecution } = useQuery(
    ['resumeParsingExecution', resumeParsingExecutionId],
    () =>
      graphQLRequest(FETCH_RESUME_PARSING_EXECUTION, {
        candidateProfileId,
        resumeParsingExecutionId,
      }),
    {
      refetchInterval: 1000,
      enabled: pollingEnabled,
      onSuccess: data => {
        fetchPollCount.current += 1

        const newStatus = data.candidateProfile.resumeParsingExecution.status
        if (newStatus === resumeParsingExecutionStatus) return

        setResumeParsingExecutionStatus(newStatus)
      },
    },
  )

  const reset = () => {
    fetchPollCount.current = 0
    setResumeParsingExecutionStatus(undefined)
    setResumeParsingExecutionId(undefined)
  }

  const hasPolledTooManyTimes = isQueued && fetchPollCount.current >= POLL_LIMIT

  const isParsingResume = isImportingResume || (isQueued && isUnderPollLimit)

  return {
    importResume,
    isParsingResume,
    resumeParsingExecution,
    resumeParsingExecutionStatus,
    hasPolledTooManyTimes,
    reset,
  }
}
